export type Periodo = 'hoje' | 'semana' | 'mes' | '7d' | '30d'

/** Preset de período ou intervalo custom (bridge: data_inicio / data_fim). */
export type PeriodQuery = {
  periodo?: Periodo
  dataInicio?: string
  dataFim?: string
}

export function periodParams(
  query: PeriodQuery,
  marketplace?: string,
): Record<string, string> {
  const params: Record<string, string> = {}
  if (query.dataInicio && query.dataFim) {
    params.data_inicio = query.dataInicio
    params.data_fim = query.dataFim
  } else {
    params.periodo = query.periodo || '7d'
  }
  if (marketplace) params.marketplace = marketplace
  // Fonte activa do BI (localStorage) — o bridge resolve source_id → db; nunca envia db_name.
  try {
    const sid = (localStorage.getItem('fiesta-bi:active-source') || 'fiesta').trim().toLowerCase()
    params.source_id = sid || 'fiesta'
  } catch {
    params.source_id = 'fiesta'
  }
  // Loja activa (Canal → loja) — store_key oficial: ml_matriz, ml_extrema, shopee_loja1/2.
  try {
    const sk = (localStorage.getItem('fiesta-bi:store-key') || '').trim()
    if (sk) params.store_key = sk
  } catch {
    /* ignore */
  }
  return params
}

export type VendasPeriodo = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace: string | null
  pedidos_validos: number
  vendas_validas: number
  vendas_validas_fmt: string
  raw?: Record<string, unknown>
}

export type PedidosMetricas = {
  periodo: { inicio: string; fim: string; label: string }
  total_liquido: number
  total_liquido_fmt: string
  total_receita: number
  total_receita_fmt: string
  total_pedidos: number
  metricas?: Record<string, number>
}

export type DailyItem = {
  date: string
  pedidosValidos?: number
  receitaValida?: number
  pedidos?: number
  receita?: number
  /**
   * Campos pedidos pela Mariana em 22/09/2026 para a tabela "Vendas Diárias" (ver
   * entrega_dev_vendas_diarias/LEIA-ME_DEV.md). Enquanto o backend não os devolver, a tabela
   * mostra "—" nessas colunas — nunca zero.
   */
  itens?: number
  /** Σ calculated_margin de `margens` type:"pedido" no dia, só pedidos com CMV. */
  margemOficial?: number
  /** MC oficial + ajuste de PIS/COFINS efetivo (+ incentivo da Extrema), regra da skill margem-ajustada-fiesta. */
  margemAjustada?: number
  /** Receita dos pedidos que têm margem — denominador da MC %. */
  receitaComMargem?: number
}

export type DashboardCompleto = {
  periodo: { inicio: string; fim: string; label: string }
  dashboard: {
    stats?: {
      totalPedidos?: number
      totalVendas?: number
      totalLiquido?: number
      totalVendasValidas?: number
      totalPedidosValidos?: number
    }
    dailyStats?: DailyItem[]
    topSkus?: Array<{
      sku: string
      descricao?: string
      quantidade?: number
      receita?: number
      catalog_sku?: string
      listing_id?: string
    }>
  }
}

export type Comparacao = {
  comparacao: {
    principal?: { totalVendasValidas?: number; totalPedidosValidos?: number }
    comparacao?: { totalVendasValidas?: number; totalPedidosValidos?: number }
    diferenca?: { totalVendas?: number; totalPedidos?: number }
  }
}

export type MargemPeriodo = {
  periodo: { inicio: string; fim: string; label: string }
  margem: {
    margemTotal?: number
    margemMedia?: number
    totalMargens?: number
    totalCMV?: number
    totalValorPago?: number
    totalImpostos?: number
    totalFretes?: number
    totalQuantidade?: number
  }
}

const BASE = (import.meta.env.VITE_BI_BRIDGE_BASE as string) || '/api/bridge'
const KEY = (import.meta.env.VITE_BI_BRIDGE_KEY as string) || ''

const CHANNELS = [
  { id: 'shopee', label: 'Shopee' },
  { id: 'ml', label: 'Mercado Livre' },
  { id: 'shein', label: 'Shein' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'tray', label: 'Tray' },
] as const

/** Lojas oficiais sob o canal (filtro store_key no bridge/S2). */
export const STORE_FILTER_OPTIONS = [
  { id: 'store:shopee_loja1', label: 'Shopee Loja 1', marketplace: 'shopee', storeKey: 'shopee_loja1' },
  { id: 'store:shopee_loja2', label: 'Shopee Loja 2', marketplace: 'shopee', storeKey: 'shopee_loja2' },
  { id: 'store:ml_matriz', label: 'ML Matriz', marketplace: 'ml', storeKey: 'ml_matriz' },
  { id: 'store:ml_extrema', label: 'ML Extrema', marketplace: 'ml', storeKey: 'ml_extrema' },
] as const

export const STORE_KEY_STORAGE = 'fiesta-bi:store-key'

export function parseCanalSelect(id: string): { marketplace: string; storeKey: string } {
  const raw = (id || '').trim()
  if (!raw) return { marketplace: '', storeKey: '' }
  const store = STORE_FILTER_OPTIONS.find((s) => s.id === raw)
  if (store) return { marketplace: store.marketplace, storeKey: store.storeKey }
  return { marketplace: raw, storeKey: '' }
}

export function canalSelectValue(marketplace: string, storeKey: string): string {
  if (storeKey) {
    const hit = STORE_FILTER_OPTIONS.find((s) => s.storeKey === storeKey)
    if (hit) return hit.id
    return `store:${storeKey}`
  }
  return marketplace || ''
}

/** Nome exibido no donut/bridge → id do filtro Canal. */
export function channelNameToId(name: string): string {
  const n = (name || '').trim().toLowerCase()
  if (!n) return ''
  if (n.includes('shopee')) return 'shopee'
  if (n.includes('mercado') || n === 'ml' || n.includes('mercadolivre')) return 'ml'
  if (n.includes('shein')) return 'shein'
  if (n.includes('amazon')) return 'amazon'
  if (n.includes('tiktok')) return 'tiktok'
  if (n.includes('tray')) return 'tray'
  const exact = CHANNELS.find((c) => c.label.toLowerCase() === n || c.id === n)
  return exact?.id || ''
}

async function bridgeGet<T>(path: string, params: Record<string, string> = {}): Promise<T> {
  const qs = new URLSearchParams(params)
  const url = `${BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}${
    qs.toString() ? `?${qs}` : ''
  }`
  const res = await fetch(url, {
    headers: {
      Accept: 'application/json',
      ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Bridge ${res.status}: ${text.slice(0, 160) || res.statusText}`)
  }
  return res.json() as Promise<T>
}

async function bridgePost<T>(path: string, body: Record<string, unknown> = {}): Promise<T> {
  const url = `${BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Bridge ${res.status}: ${text.slice(0, 160) || res.statusText}`)
  }
  return res.json() as Promise<T>
}

async function bridgePatch<T>(path: string, body: Record<string, unknown> = {}): Promise<T> {
  const url = `${BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Bridge ${res.status}: ${text.slice(0, 160) || res.statusText}`)
  }
  return res.json() as Promise<T>
}

async function bridgeDelete<T>(path: string): Promise<T> {
  const url = `${BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Bridge ${res.status}: ${text.slice(0, 160) || res.statusText}`)
  }
  return res.json() as Promise<T>
}

export { bridgeGet, bridgePost, bridgePatch, bridgeDelete }

export function fetchVendas(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<VendasPeriodo>('/vendas/periodo', periodParams(q, marketplace))
}

export function fetchMetricas(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<PedidosMetricas>('/pedidos/metricas', periodParams(q, marketplace))
}

export function fetchDashboard(
  query: PeriodQuery | Periodo,
  marketplace?: string,
  opts?: { enrich?: boolean },
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  const params = periodParams(q, marketplace)
  if (opts?.enrich === false) params.enrich = '0'
  return bridgeGet<DashboardCompleto>('/vendas/dashboard-completo', params)
}

export function fetchComparacao(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<Comparacao>('/vendas/comparacao', periodParams(q, marketplace))
}

/**
 * Comparação contra uma janela escolhida (mês passado, ano passado…).
 *
 * O bridge (`resolve_comparison`) só assume "período anterior" quando as datas
 * de comparação **não** chegam; mandando as quatro ele compara o que pedirmos.
 * É o que destrava mês-a-mês sem tocar no backend.
 */
export function fetchComparacaoRange(
  principal: { inicio: string; fim: string },
  comparacao: { inicio: string; fim: string },
  marketplace?: string,
) {
  const params = periodParams({ dataInicio: principal.inicio, dataFim: principal.fim }, marketplace)
  delete params.data_inicio
  delete params.data_fim
  params.data_inicio_principal = principal.inicio
  params.data_fim_principal = principal.fim
  params.data_inicio_comparacao = comparacao.inicio
  params.data_fim_comparacao = comparacao.fim
  return bridgeGet<Comparacao>('/vendas/comparacao', params)
}

/** Série diária de um intervalo qualquer — usada para desenhar a linha comparada. */
export async function fetchDailyRange(
  inicio: string,
  fim: string,
  marketplace?: string,
): Promise<Array<{ data: string; valor: number; pedidos: number }>> {
  const dash = await fetchDashboard({ dataInicio: inicio, dataFim: fim }, marketplace, {
    enrich: false,
  })
  return (dash.dashboard?.dailyStats || []).map((d) => ({
    data: String(d.date || ''),
    valor: Number(d.receitaValida ?? d.receita ?? 0),
    pedidos: Number(d.pedidosValidos ?? d.pedidos ?? 0),
  }))
}

export function fetchMargens(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<MargemPeriodo>('/margens/periodo', periodParams(q, marketplace))
}

export const MARKETPLACE_OPTIONS = [
  { id: '', label: 'Todos os canais' },
  ...CHANNELS.map((c) => ({ id: c.id, label: c.label })),
] as const

export type MarketplaceId = (typeof CHANNELS)[number]['id'] | ''

/** Rótulo amigável do filtro Canal (canal ou loja). */
export function canalFilterLabel(selectId: string): string {
  const raw = (selectId || '').trim()
  if (!raw) return 'Todos os canais'
  const store = STORE_FILTER_OPTIONS.find((s) => s.id === raw)
  if (store) return store.label
  const ch = MARKETPLACE_OPTIONS.find((o) => o.id === raw)
  return ch?.label || raw
}

/** Lojas sob um canal (para submenu no hover). */
export function storesForChannel(marketplaceId: string) {
  return STORE_FILTER_OPTIONS.filter((s) => s.marketplace === marketplaceId)
}

export async function fetchCanais(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  try {
    const res = await bridgeGet<{
      canais?: Array<{
        nome?: string
        name?: string
        vendas?: number
        value?: number
        pedidos?: number
      }>
      capability_status?: string
    }>('/vendas/canais', {
      ...periodParams(q, marketplace),
      limite: '15',
    })
    if (res.capability_status === 'unavailable') return []
    return (res.canais || [])
      .map((c) => {
        const name = String(c.name || c.nome || '').trim() || '(sem canal)'
        return {
          id: name,
          name,
          value: Number(c.value ?? c.vendas ?? 0),
          pedidos: Number(c.pedidos || 0),
        }
      })
      .filter((r) => r.value > 0 || r.pedidos > 0)
  } catch {
    return []
  }
}

export type BiOverview = {
  periodo: Periodo | 'custom'
  marketplace: string | null
  rangeLabel: string
  /** Datas ISO do período efetivamente resolvido pelo bridge (`YYYY-MM-DD`).
   *  Vazias quando o backend não devolveu — a Home precisa delas para montar a
   *  janela de comparação, e `rangeLabel` já vem formatado demais para isso. */
  rangeInicio: string
  rangeFim: string
  vendasFmt: string
  vendas: number
  liquidoFmt: string
  liquido: number
  pedidos: number
  /** Pedidos do período **antes** do corte de validade. A diferença para
   *  `pedidos` é o que o backend não contou como válido — cancelado, não pago,
   *  devolvido. O bridge não diz qual é qual, então a tela mostra a diferença
   *  como "não válidos" e não afirma "cancelados". 0 quando não veio. */
  pedidosTotais: number
  taxas: number
  taxasFmt: string
  frete: number
  freteFmt: string
  margemFmt: string
  margemTotal: number
  /** Receita **dos pedidos que têm margem calculada** (`totalValorPago`).
   *  É o denominador certo da MC %: dividir por `vendas` misturaria pedidos sem
   *  margem no de baixo e não no de cima, achatando o percentual. */
  receitaComMargem: number
  cmvFmt: string
  cmvTotal: number
  margemMediaFmt: string
  totalMargens: number
  coberturaPct: number | null
  /** CMV agregado absurdo vs vendas (cadastro de custo) — não tratar como P&L */
  cmvInconsistente?: boolean
  prevVendas: number | null
  prevVendasFmt: string | null
  prevPedidos: number | null
  deltaVendasPct: number | null
  deltaPedidosPct: number | null
  /** `dia` é o rótulo curto ("seg, 15/09"); `data` é o ISO original, que a
   *  tabela e o alinhamento das duas séries do gráfico precisam. */
  daily: Array<{
    dia: string
    data: string
    valor: number
    pedidos: number
    itens?: number | null
    mc?: number | null
    mcAj?: number | null
    recMc?: number | null
  }>
  canais: Array<{ name: string; value: number; pedidos: number }>
  canaisConsistentes?: boolean
  topSkus: Array<{
    sku: string
    descricao: string
    receita: number
    quantidade: number
    catalog_sku?: string
    listing_id?: string
  }>
}

export type SyncStatusPayload = {
  ok?: boolean
  error?: string
  message?: string
  sync?: {
    running?: boolean
    progress?: number
    message?: string
    last_sync?: string | null
    last_sync_source?: string | null
    last_sync_marketplace?: string | null
    last_sync_efetiva?: string | null
    last_sync_efetiva_origem?: string | null
    last_activity_at?: string | null
    last_activity_marketplace?: string | null
    last_activity_summary?: string | null
    errors?: unknown[]
    sync_orders_processed?: number
    sync_orders_total?: number
  }
}

export type LojasPayload = {
  ok?: boolean
  s2_platform_stores?: Array<{
    key?: string
    label?: string
    stores?: Array<{ id?: string; name?: string; label?: string }>
  }>
  s1_marketplaces?: unknown
  s1_error?: string
  s1_note?: string
  s2_error?: string
}

export type OpsAlert = {
  severity: 'error' | 'warning' | 'info'
  code: string
  title: string
  detail: string
  at?: string
  marketplace?: string
  ocorrencias?: number
  desde?: string
}

export type MarginCoverage = {
  pedidos: number
  margens_registros: number
  cmv_total: number
  cmv_total_fmt: string
  margem_total: number
  margem_total_fmt: string
  cobertura_pct: number | null
  sem_margem: boolean
  sem_cmv: boolean
}

export type OpsAlertsPayload = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace: string | null
  margin_coverage?: MarginCoverage
  margin_gap?: {
    pedidos?: number
    com_margem?: number
    sem_nf?: number
    nf_sem_margem?: number
    sem_nf_por_canal?: Record<string, number>
    sem_nf_hoje_por_canal?: Record<string, number>
    nf_sem_margem_por_canal?: Record<string, number>
    causa_principal?: string
    upseller_stalled?: boolean
    upseller_detail?: string
    nota?: string
  }
  alerts: OpsAlert[]
}

export function fetchOpsAlerts(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<OpsAlertsPayload>('/ops/alerts', periodParams(q, marketplace))
}

export type InsightDominio =
  | 'home'
  | 'comercial'
  | 'mix'
  | 'logistica'
  | 'financeiro'
  | 'fiscal'
  | 'operacoes'
  | 'clientes'
  | 'despesas'
  | 'agenda'

export type InsightAchado = {
  titulo: string
  detalhe: string
  impacto?: 'alto' | 'medio' | 'baixo'
  destino?: string
  valor?: number
}

export type InsightAcao = InsightAchado & {
  dominio: InsightDominio
  dominio_label?: string
  score: number
  ordem_origem?: number
}

export type InsightPlano = {
  periodo?: { inicio?: string; fim?: string; label?: string }
  marketplace?: string | null
  acoes: InsightAcao[]
  total: number
  confianca?: { nivel?: 'alta' | 'media' | 'baixa'; motivo?: string }
  texto?: string
  fonte?: string
}

export type InsightResumo = {
  dominio: InsightDominio
  dominio_label?: string
  periodo?: { inicio?: string; fim?: string; label?: string }
  marketplace?: string | null
  titulo: string
  leitura: string
  insight: string
  principal: InsightAchado
  achados: InsightAchado[]
  recomendacoes: InsightAchado[]
  proxima_acao?: { titulo?: string; detalhe?: string; destino?: string }
  confianca?: { nivel?: 'alta' | 'media' | 'baixa'; motivo?: string }
  texto?: string
  fonte?: string
}

/** «Insight da IA»: mesma leitura que o Hermes dá no chat (bridge `/insights/resumo`). */
export function fetchInsightResumo(
  dominio: InsightDominio,
  query: PeriodQuery | Periodo,
  marketplace?: string,
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<InsightResumo>('/insights/resumo', {
    ...periodParams(q, marketplace),
    dominio,
  })
}

/** Plano transversal priorizado pela mesma lógica dos painéis e do Hermes. */
export function fetchInsightPlano(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<InsightPlano>('/insights/plano', periodParams(q, marketplace))
}

export function fetchSyncStatus() {
  return bridgeGet<SyncStatusPayload>('/sync/status')
}

export function fetchLojas() {
  return bridgeGet<LojasPayload>('/lojas')
}

/** Um caso da collection `reclamacoes` — Mercado Livre e Shopee na mesma forma. */
export type CasoBridge = {
  canal: 'mercado_livre' | 'shopee'
  conta: string
  loja?: string
  /** backend grava camelCase; snake_case fica por compat */
  claim_id?: string
  claimId?: string
  order_id?: string
  orderId?: string
  aberta_em?: string | null
  abertaEm?: string | null
  status?: string
  aberta?: boolean
  stage?: string
  motivo_cod?: string
  motivoCod?: string
  motivo_label?: string
  motivoLabel?: string
  hot?: boolean
  valor?: number | null
  mlb?: string | null
  item_titulo?: string | null
  itemTitulo?: string | null
  pendente?: boolean
  prazo?: {
    tem_prazo?: boolean
    temPrazo?: boolean
    vence_em?: string | null
    venceEm?: string | null
    respondido_em?: string | null
    respondidoEm?: string | null
    /** 1 dentro, 0 perdida, -1 não se aplica */
    no_prazo?: number
    noPrazo?: number
    /** prazo do ML sai do texto do mediador, não de um campo */
    heuristica?: boolean
  } | null
  mensagens?: Array<{ de?: string; em?: string | null; texto?: string }>
  /** desfecho aplicado; só existe em caso encerrado (vem do histórico) */
  solucao?: {
    aplicada?: boolean
    tipo?: string | null
    label?: string | null
    em?: string | null
    valor?: number | string | null
  } | null
  coletado_em?: string
  coletadoEm?: string
}

export type ReclamacoesPayload = {
  meta: {
    coletado_em?: string | null
    periodo?: { de?: string | null; ate?: string | null }
    total?: number
    /** de onde veio: consulta à API agora, ou retrato gravado no banco */
    origem?: 'api' | 'banco' | 'api+banco'
    /** canal que não respondeu, fila servida de retrato, etc. */
    avisos?: string[]
    is_sample?: boolean
  }
  casos: CasoBridge[]
  itens: Array<{ mlb: string; titulo: string }>
}

/**
 * Base da API do fiesta-financial (Node/Express), onde vivem as reclamações.
 *
 * É outro serviço do bridge: o bridge é o Python na 8765, este é o Node na 8080
 * sob /api/v1. O nginx do BI precisa de uma `location /api/v1/` apontando para
 * lá — hoje ele só roteia /api/bridge/.
 */
const API_BASE = (import.meta.env.VITE_FINANCIAL_API_BASE as string) || '/api/v1'

/**
 * O nginx do BI valida a sessão via auth_request → /auth/check, mas o Set-Cookie
 * desse subrequest NÃO chega ao browser. Fica só o refresh. A API reclamações
 * exige fiesta_access_token — sem este ping, cai sempre em 401 e no JSON estático.
 */
let sessaoFinanceira: Promise<void> | null = null

/** Invalida o cache do ping /auth/check (após login/logout). */
export function resetSessaoFinanceira() {
  sessaoFinanceira = null
}

function garantirSessaoFinanceira(): Promise<void> {
  if (!sessaoFinanceira) {
    sessaoFinanceira = fetch(`${API_BASE.replace(/\/$/, '')}/auth/check`, {
      headers: { Accept: 'application/json' },
      credentials: 'include',
      cache: 'no-store', // sem isto o browser manda If-None-Match e o nginx auth_request trata 304 como 500
    })
      .then((res) => {
        if (!res.ok) throw new Error(`API ${res.status}: sessão financeira inválida`)
      })
      .catch((err) => {
        sessaoFinanceira = null
        throw err
      })
  }
  return sessaoFinanceira
}

async function apiGet<T>(path: string, params: Record<string, string> = {}): Promise<T> {
  await garantirSessaoFinanceira()
  const qs = new URLSearchParams(params)
  const url = `${API_BASE.replace(/\/$/, '')}${path}${qs.toString() ? `?${qs}` : ''}`
  const res = await fetch(url, {
    headers: { Accept: 'application/json', ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}) },
    credentials: 'include', // a API do financial autentica por sessão/cookie
    cache: 'no-store',
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${text.slice(0, 160) || res.statusText}`)
  }
  const json = (await res.json()) as { success?: boolean; data?: T } & T
  // fiesta-financial envolve em { success, data }; o painel espera o payload interno
  if (json && typeof json === 'object' && 'data' in json && json.data !== undefined) {
    return json.data as T
  }
  return json as T
}

/**
 * Reclamações e devoluções.
 *
 * `fonte: 'api'` (padrão no backend) consulta os marketplaces na hora — é o que
 * a fila de casos em aberto exige, porque o prazo do mediador é de horas.
 * `fonte: 'banco'` lê o histórico gravado, que é o certo para a análise do
 * período: aquilo não muda mais e não vale uma varredura de API por tela aberta.
 */
export function fetchReclamacoes(opts: {
  de?: string
  ate?: string
  canal?: 'ml' | 'shopee'
  conta?: string
  aberta?: boolean
  motivo?: string
  fonte?: 'api' | 'banco'
} = {}) {
  const params: Record<string, string> = {}
  if (opts.de) params.de = opts.de
  if (opts.ate) params.ate = opts.ate
  if (opts.canal) params.canal = opts.canal
  if (opts.conta) params.conta = opts.conta
  if (opts.motivo) params.motivo = opts.motivo
  if (opts.fonte) params.fonte = opts.fonte
  if (opts.aberta !== undefined) params.aberta = opts.aberta ? '1' : '0'
  return apiGet<ReclamacoesPayload>('/reclamacoes', params)
}

function fmtDay(iso: string): string {
  try {
    const d = new Date(`${iso}T12:00:00`)
    return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })
  } catch {
    return iso
  }
}

function pctDelta(atual: number, anterior: number): number | null {
  if (!anterior) return atual ? 100 : null
  return ((atual - anterior) / anterior) * 100
}

function assembleOverview(
  q: PeriodQuery,
  mp: string | undefined,
  vendas: VendasPeriodo,
  metricas: PedidosMetricas | null,
  dash: DashboardCompleto,
  comp: Comparacao | null,
  margens: MargemPeriodo | null,
  canais: BiOverview['canais'],
): BiOverview {
  const principal = comp?.comparacao?.principal?.totalVendasValidas
  const previo = comp?.comparacao?.comparacao?.totalVendasValidas
  const prevPedidosRaw = comp?.comparacao?.comparacao?.totalPedidosValidos
  const pedidosAtual = Number(
    vendas.pedidos_validos ||
      metricas?.total_pedidos ||
      comp?.comparacao?.principal?.totalPedidosValidos ||
      0,
  )
  const delta =
    principal != null && previo != null ? pctDelta(Number(principal), Number(previo)) : null
  const deltaPedidos =
    prevPedidosRaw != null ? pctDelta(pedidosAtual, Number(prevPedidosRaw)) : null

  const margemTotal = Number(margens?.margem?.margemTotal || 0)
  const receitaComMargem = Number(margens?.margem?.totalValorPago || 0)
  const cmvTotal = Number(margens?.margem?.totalCMV || 0)
  const margemMedia = Number(margens?.margem?.margemMedia || 0)
  const totalMargens = Number(margens?.margem?.totalMargens || 0)
  const coberturaPct =
    pedidosAtual > 0 ? Math.round((totalMargens / pedidosAtual) * 1000) / 10 : null
  const vendasNum = Number(vendas.vendas_validas || 0)
  const cmvInconsistente =
    vendasNum > 0 &&
    (cmvTotal > vendasNum * 1.5 || margemTotal < -vendasNum * 0.5)

  const m = metricas?.metricas || {}
  const taxas = Number(m.totalTaxas ?? 0)
  const frete = Number(m.totalFrete ?? 0)

  const daily = (dash.dashboard?.dailyStats || []).map((d) => ({
    dia: fmtDay(d.date),
    data: String(d.date || ''),
    valor: Number(d.receitaValida ?? d.receita ?? 0),
    pedidos: Number(d.pedidosValidos ?? d.pedidos ?? 0),
    // ausente = o backend ainda não devolve; fica null (a tabela escreve "—"), nunca 0
    itens: d.itens == null ? null : Number(d.itens),
    mc: d.margemOficial == null ? null : Number(d.margemOficial),
    mcAj: d.margemAjustada == null ? null : Number(d.margemAjustada),
    recMc: d.receitaComMargem == null ? null : Number(d.receitaComMargem),
  }))

  const topSkus = (dash.dashboard?.topSkus || []).slice(0, 5).map((s) => ({
    sku: String(s.sku || ''),
    descricao: String(s.descricao || s.sku || ''),
    receita: Number(s.receita || 0),
    quantidade: Number(s.quantidade || 0),
    catalog_sku: s.catalog_sku ? String(s.catalog_sku) : undefined,
    listing_id: s.listing_id ? String(s.listing_id) : undefined,
  }))

  const p = vendas.periodo
  const rangeInicio = String(p?.inicio || q.dataInicio || '')
  const rangeFim = String(p?.fim || q.dataFim || '')
  const rangeLabel =
    rangeInicio && rangeFim ? `${rangeInicio} → ${rangeFim}` : q.periodo || 'custom'

  const liquidoFiltrado = Number(dash.dashboard?.stats?.totalLiquido || 0)
  const liquido = metricas ? Number(metricas.total_liquido || 0) : liquidoFiltrado
  const prevVendas = previo != null ? Number(previo) : null

  return {
    periodo: q.dataInicio && q.dataFim ? 'custom' : q.periodo || '7d',
    marketplace: mp || null,
    rangeLabel,
    rangeInicio,
    rangeFim,
    vendasFmt: vendas.vendas_validas_fmt || fmtBrl(vendas.vendas_validas),
    vendas: Number(vendas.vendas_validas || 0),
    liquidoFmt: fmtBrl(liquido),
    liquido,
    pedidos: pedidosAtual,
    pedidosTotais: Number(dash.dashboard?.stats?.totalPedidos || 0),
    taxas,
    taxasFmt: fmtBrl(taxas),
    frete,
    freteFmt: fmtBrl(frete),
    margemFmt: fmtBrl(margemTotal),
    margemTotal,
    receitaComMargem,
    cmvFmt: fmtBrl(cmvTotal),
    cmvTotal,
    margemMediaFmt: `${margemMedia.toFixed(1)}%`,
    totalMargens,
    coberturaPct,
    cmvInconsistente,
    prevVendas,
    prevVendasFmt: prevVendas != null ? fmtBrl(prevVendas) : null,
    prevPedidos: prevPedidosRaw != null ? Number(prevPedidosRaw) : null,
    deltaVendasPct: delta,
    deltaPedidosPct: deltaPedidos,
    daily,
    canais,
    topSkus,
  }
}

export function patchOverviewMeta(
  overview: BiOverview,
  comp: Comparacao | null,
  canais: BiOverview['canais'],
): BiOverview {
  const principal = comp?.comparacao?.principal?.totalVendasValidas
  const previo = comp?.comparacao?.comparacao?.totalVendasValidas
  const prevPedidosRaw = comp?.comparacao?.comparacao?.totalPedidosValidos
  const prevVendas = previo != null ? Number(previo) : overview.prevVendas
  const pedidosAtual = Number(overview.pedidos || 0)
  const delta =
    principal != null && previo != null ? pctDelta(Number(principal), Number(previo)) : overview.deltaVendasPct
  const prevPedidos = prevPedidosRaw != null ? Number(prevPedidosRaw) : overview.prevPedidos
  const deltaPedidos =
    prevPedidos != null && prevPedidos > 0
      ? pctDelta(pedidosAtual, prevPedidos)
      : overview.deltaPedidosPct
  const totalCanais = canais.reduce((sum, canal) => sum + Number(canal.value || 0), 0)
  const toleranciaCanais = Math.max(0.02, Math.abs(overview.vendas) * 0.005)
  const canaisConsistentes =
    canais.length > 0
      ? Math.abs(totalCanais - overview.vendas) <= toleranciaCanais
      : Math.abs(overview.vendas) <= toleranciaCanais
  return {
    ...overview,
    canais: canaisConsistentes && canais.length ? canais : overview.canais,
    canaisConsistentes,
    prevVendas,
    prevVendasFmt: prevVendas != null ? fmtBrl(prevVendas) : overview.prevVendasFmt,
    prevPedidos,
    deltaVendasPct: delta,
    deltaPedidosPct: deltaPedidos,
  }
}

/** KPIs principais (rápido) — delta/canais e gráficos vêm depois. */
export async function loadOverviewKpis(
  query: PeriodQuery | Periodo,
  marketplace?: string | null,
): Promise<BiOverview> {
  const q: PeriodQuery = typeof query === 'string' ? { periodo: query } : query
  const mp = marketplace?.trim() || undefined
  const [vendas, metricas, margens] = await Promise.all([
    fetchVendas(q, mp),
    fetchMetricas(q, mp).catch(() => null),
    fetchMargens(q, mp).catch(() => null),
  ])
  const emptyDash: DashboardCompleto = {
    periodo: vendas.periodo,
    dashboard: { stats: {}, dailyStats: [], topSkus: [] },
  }
  return assembleOverview(q, mp, vendas, metricas, emptyDash, null, margens, [])
}

export function patchOverviewCharts(overview: BiOverview, dash: DashboardCompleto): BiOverview {
  const daily = (dash.dashboard?.dailyStats || []).map((d) => ({
    dia: fmtDay(d.date),
    data: String(d.date || ''),
    valor: Number(d.receitaValida ?? d.receita ?? 0),
    pedidos: Number(d.pedidosValidos ?? d.pedidos ?? 0),
    // ausente = o backend ainda não devolve; fica null (a tabela escreve "—"), nunca 0
    itens: d.itens == null ? null : Number(d.itens),
    mc: d.margemOficial == null ? null : Number(d.margemOficial),
    mcAj: d.margemAjustada == null ? null : Number(d.margemAjustada),
    recMc: d.receitaComMargem == null ? null : Number(d.receitaComMargem),
  }))
  const topSkus = (dash.dashboard?.topSkus || []).slice(0, 5).map((s) => ({
    sku: String(s.sku || ''),
    descricao: String(s.descricao || s.sku || ''),
    receita: Number(s.receita || 0),
    quantidade: Number(s.quantidade || 0),
    catalog_sku: s.catalog_sku ? String(s.catalog_sku) : undefined,
    listing_id: s.listing_id ? String(s.listing_id) : undefined,
  }))
  const liquidoFiltrado = Number(dash.dashboard?.stats?.totalLiquido || 0)
  return {
    ...overview,
    daily,
    topSkus,
    // `loadOverviewKpis` monta o overview com um dash vazio; o total de pedidos
    // só existe aqui, quando o dashboard completo chega.
    pedidosTotais: Number(dash.dashboard?.stats?.totalPedidos || overview.pedidosTotais || 0),
    liquido: overview.liquido || liquidoFiltrado,
    liquidoFmt: overview.liquido ? overview.liquidoFmt : fmtBrl(liquidoFiltrado),
  }
}

export async function loadOverview(
  query: PeriodQuery | Periodo,
  marketplace?: string | null,
): Promise<BiOverview> {
  const q: PeriodQuery = typeof query === 'string' ? { periodo: query } : query
  const mp = marketplace?.trim() || undefined
  const kpis = await loadOverviewKpis(q, mp)
  try {
    const [dash, comp, canais] = await Promise.all([
      fetchDashboard(q, mp, { enrich: false }),
      fetchComparacao(q, mp).catch(() => null),
      fetchCanais(q, mp),
    ])
    return patchOverviewMeta(patchOverviewCharts(kpis, dash), comp, canais)
  } catch {
    return kpis
  }
}

export function fmtBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export type BiSource = {
  id: string
  name: string
  db_name: string
  builtin?: boolean
  status?: string
  role?: string
  logo_url?: string | null
  pedidos_count?: number
  visible_in_bi?: boolean
  default_in_bi?: boolean
  sort_order?: number
}

export type FontesResponse = {
  ok?: boolean
  fontes: BiSource[]
  total?: number
}

export function fetchFontes(includeCounts = false) {
  return bridgeGet<FontesResponse>('/fontes', includeCounts ? { counts: '1' } : {})
}

export function createFonte(payload: {
  name: string
  slug?: string
  role?: string
  logo_url?: string
  origin_product_id?: string
  actor?: string
  skip_smoke?: boolean
}) {
  return bridgePost<{
    ok: boolean
    fonte: BiSource
    error?: string
    bind_s1?: {
      ok?: boolean
      error?: string
      connection_id?: string
      authz_status?: string
      smoke?: { ok?: boolean; pedidos_na_pagina?: number }
      note?: string
      token_exposed?: boolean
    } | null
  }>('/fontes', payload)
}

export function bindFonteS1(
  id: string,
  payload?: { actor?: string; skip_env_write?: boolean; skip_smoke?: boolean },
) {
  return bridgePost<{
    ok: boolean
    bind_s1?: {
      ok?: boolean
      error?: string
      connection_id?: string
      authz_status?: string
      smoke?: { ok?: boolean; pedidos_na_pagina?: number }
      token_exposed?: boolean
    }
    error?: string
  }>(`/fontes/${encodeURIComponent(id)}/bind-s1`, payload || {})
}

export function updateFonte(
  id: string,
  payload: {
    name?: string
    role?: string
    logo_url?: string
    slug?: string
    visible_in_bi?: boolean
    default_in_bi?: boolean
    sort_order?: number
  },
) {
  return bridgePatch<{ ok: boolean; fonte: BiSource; error?: string }>(
    `/fontes/${encodeURIComponent(id)}`,
    payload,
  )
}

export function deleteFonte(id: string) {
  return bridgeDelete<{ ok: boolean; id?: string; error?: string }>(
    `/fontes/${encodeURIComponent(id)}`,
  )
}

export type SourceStatusPayload = {
  ok?: boolean
  error?: string
  source_id?: string
  health?: 'ok' | 'warning' | 'error' | 'suspended' | string
  shadow?: boolean
  source?: {
    id?: string
    name?: string
    db_name?: string
    builtin?: boolean
    role?: string
    status?: string
    is_mirror?: boolean
    kill_switch_reason?: string | null
    kill_switch_at?: string | null
  }
  kill_switch?: {
    suspended?: boolean
    reason?: string | null
    at?: string | null
    fiesta_protected?: boolean
    resume_hint?: string | null
  }
  identity?: {
    org_id?: string | null
    unit_id?: string | null
    origin_product_id?: string | null
    connection_id?: string | null
    names?: {
      org?: string
      unit?: string
      origin_product?: string
      vendor?: string
    } | null
  }
  authorization?: {
    status?: string
    id?: string | null
    approved_by?: string
    approved_at?: string
    purpose?: string
    scope_mode?: string
    retention_days?: number
    missing?: boolean
    note?: string
  }
  connections?: Array<{
    id?: string
    kind?: string
    status?: string
    label?: string
  }>
  freshness?: {
    field?: string | null
    at?: string | null
    age_minutes?: number | null
    pedidos_count?: number
    ok?: boolean
    sla_minutes?: number
    state?: 'fresh' | 'stale' | 'critical' | 'unknown' | 'suspended' | string
  }
  coverage?: {
    pedidos_count?: number
    quality_suggestion?: string | null
    recon_ok?: boolean | null
    capabilities?: string[]
    capabilities_count?: number
  }
  drift?: {
    last?: {
      id?: string
      at?: string
      severity?: string
      alert_count?: number
      suggest_kill_switch?: boolean
      codes?: string[]
    } | null
  }
  semantics?: {
    last_run_id?: string
    quality_suggestion?: string | null
    recon_ok?: boolean | null
  }
  provenance?: {
    label_pt?: string
    contract_hint?: string | null
  }
}

/** Fase 8 — status/proveniência da fonte activa. */
export function fetchSourceStatus(sourceId?: string) {
  const sid = (sourceId || '').trim() || 'fiesta'
  return bridgeGet<SourceStatusPayload>('/fonte/status', { source_id: sid })
}

/** DoD #1 — organizações / unidades / produtos de origem. */
export function fetchOrganizacoes() {
  return bridgeGet<{ ok?: boolean; organizacoes: Array<{ id: string; name: string; status?: string }>; total?: number }>(
    '/organizacoes',
  )
}

export function fetchUnidades(orgId?: string) {
  return bridgeGet<{ ok?: boolean; unidades: Array<{ id: string; org_id: string; name: string }>; total?: number }>(
    '/unidades',
    orgId ? { org_id: orgId } : {},
  )
}

export function fetchProdutosOrigem() {
  return bridgeGet<{
    ok?: boolean
    produtos_origem: Array<{ id: string; name: string; vendor?: string }>
    total?: number
  }>('/produtos-origem')
}

export function fetchAutorizacoes(sourceId?: string) {
  return bridgeGet<{
    ok?: boolean
    autorizacoes: Array<{
      id: string
      source_id?: string
      status?: string
      purpose?: string
      approved_by?: string
      scope?: { mode?: string }
    }>
    total?: number
  }>('/autorizacoes', sourceId ? { source_id: sourceId } : {})
}

export function seedOrganizacoes(actor = 'bi-wizard') {
  return bridgePost<{ ok?: boolean; identity?: unknown; authorizations?: unknown }>(
    '/organizacoes/seed',
    { actor },
  )
}

export function fetchCapabilities(sourceId?: string) {
  const sid = (sourceId || '').trim() || 'fiesta'
  return bridgeGet<{
    ok?: boolean
    source_id?: string
    capabilities?: Record<string, { status?: string; reason_code?: string; quality?: string }>
  }>('/capabilities', { source_id: sid })
}

export function fetchConnectorObservability(sourceId?: string) {
  const sid = (sourceId || '').trim() || 'fiesta'
  return bridgeGet<{
    ok?: boolean
    source_id?: string
    lag_state?: string
    availability?: string
    last_run?: {
      status?: string
      at?: string
      duration_ms?: number
      records_written?: number
      reason_codes?: string[]
    } | null
  }>('/connectors/observability', { source_id: sid })
}

export function fetchConexoes(sourceId?: string) {
  return bridgeGet<{
    ok?: boolean
    conexoes: Array<{ id: string; source_id?: string; kind?: string; label?: string; status?: string }>
    total?: number
  }>('/conexoes', sourceId ? { source_id: sourceId } : {})
}

/** Overview vazio para fontes externas (bridge multi-DB = fatia C). */
export function buildEmptyOverview(
  query: PeriodQuery | Periodo,
  marketplace?: string | null,
): BiOverview {
  const q: PeriodQuery = typeof query === 'string' ? { periodo: query } : query
  const rangeLabel =
    q.dataInicio && q.dataFim
      ? `${q.dataInicio} → ${q.dataFim}`
      : PERIODO_OPTIONS.find((o) => o.id === q.periodo)?.label || q.periodo || '—'
  return {
    periodo: q.dataInicio && q.dataFim ? 'custom' : q.periodo || '7d',
    marketplace: marketplace?.trim() || null,
    rangeLabel,
    rangeInicio: q.dataInicio || '',
    rangeFim: q.dataFim || '',
    vendasFmt: fmtBrl(0),
    vendas: 0,
    liquidoFmt: fmtBrl(0),
    pedidosTotais: 0,
    liquido: 0,
    pedidos: 0,
    taxas: 0,
    taxasFmt: fmtBrl(0),
    frete: 0,
    freteFmt: fmtBrl(0),
    margemFmt: fmtBrl(0),
    margemTotal: 0,
    receitaComMargem: 0,
    cmvFmt: fmtBrl(0),
    cmvTotal: 0,
    margemMediaFmt: '0,0%',
    totalMargens: 0,
    coberturaPct: null,
    prevVendas: null,
    prevVendasFmt: null,
    prevPedidos: null,
    deltaVendasPct: null,
    deltaPedidosPct: null,
    daily: [],
    canais: [],
    topSkus: [],
  }
}

export const PERIODO_OPTIONS: Array<{ id: Periodo; label: string }> = [
  { id: 'hoje', label: 'Hoje' },
  { id: 'semana', label: 'Esta semana' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: 'mes', label: 'Este mês' },
  { id: '30d', label: 'Últimos 30 dias' },
]

export type AssistantChatResponse = {
  ok: boolean
  reply?: string
  session_id?: string
  error?: string
  message?: string
  llm_model?: string | null
}

export type AssistantTranscribeResponse = {
  ok: boolean
  transcript?: string
  model?: string
  provider?: string
  error?: string
  message?: string
}

export type BiChatContext = {
  page?: string
  insight_dominio?: InsightDominio
  periodo?: string
  data_inicio?: string
  data_fim?: string
  range_label?: string
  marketplace?: string | null
  marketplace_label?: string
  /** Fonte activa no BI — o bridge/Hermes nunca misturam fontes. */
  source_id?: string
  source_name?: string
  source_db_name?: string
  vendas_fmt?: string
  liquido_fmt?: string
  taxas_fmt?: string
  frete_fmt?: string
  margem_fmt?: string
  cmv_fmt?: string
  cmv_inconsistente?: boolean
  cobertura_pct?: number | null
  total_margens?: number
  pedidos?: number
  canais?: Array<{ name: string; value: number; vendas_fmt?: string }>
  canais_consistentes?: boolean
}

export type AssistantHistoryMessage = {
  id: string
  role: 'user' | 'assistant' | 'system'
  text: string
  at?: string
  channel?: string
}

export type AssistantHistoryResponse = {
  ok: boolean
  owner_id?: string
  session_id?: string | null
  wiped_at?: string | null
  messages?: AssistantHistoryMessage[]
  error?: string
  message?: string
}

export async function fetchAssistantHistory(): Promise<AssistantHistoryResponse> {
  return bridgeGet<AssistantHistoryResponse>('/assistant/historico')
}

export async function importAssistantHistory(
  messages: AssistantHistoryMessage[],
  sessionId?: string | null,
): Promise<AssistantHistoryResponse> {
  return bridgePost<AssistantHistoryResponse>('/assistant/historico', {
    action: 'import',
    messages,
    ...(sessionId ? { session_id: sessionId } : {}),
  })
}

export async function clearAssistantHistory(): Promise<AssistantHistoryResponse> {
  return bridgePost<AssistantHistoryResponse>('/assistant/historico', { action: 'limpar' })
}

export type OrgLlmSettings = {
  ok?: boolean
  org_id: string
  billing_mode: 'platform' | 'byok'
  provider: string
  has_api_key: boolean
  api_key_last4?: string | null
  default_model: string
  allowed_models: string[]
  monthly_budget_usd?: number | null
  soft_alert_pct?: number
  status: string
  usage?: {
    period?: string
    spent_usd?: number
    calls?: number
    tokens_in?: number
    tokens_out?: number
    budget_usd?: number | null
    pct?: number | null
  } | null
  providers?: Array<{ id: string; label: string }>
  product_models?: Array<{ id: string; label: string; hint?: string; provider?: string }>
  error?: string
}

export async function fetchOrgLlmSettings(params: {
  orgId?: string
  sourceId?: string
} = {}): Promise<OrgLlmSettings> {
  const q: Record<string, string> = {}
  if (params.orgId) q.org_id = params.orgId
  if (params.sourceId) q.source_id = params.sourceId
  return bridgeGet<OrgLlmSettings>('/org/llm-settings', q)
}

export async function saveOrgLlmSettings(body: {
  org_id?: string
  source_id?: string
  billing_mode?: 'platform' | 'byok'
  provider?: string
  default_model?: string
  allowed_models?: string[]
  api_key?: string
  clear_api_key?: boolean
  monthly_budget_usd?: number | null | ''
  soft_alert_pct?: number
  reset_usage?: boolean
  status?: string
}): Promise<OrgLlmSettings> {
  return bridgePost<OrgLlmSettings>('/org/llm-settings', body)
}

export async function sendAssistantChat(
  message: string,
  sessionId?: string | null,
  context?: BiChatContext | null,
  model?: string | null,
): Promise<AssistantChatResponse> {
  const url = `${BASE.replace(/\/$/, '')}/assistant/chat`
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 160_000)
  try {
    const res = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
      },
      body: JSON.stringify({
        message,
        ...(sessionId ? { session_id: sessionId } : {}),
        ...(context ? { context } : {}),
        ...(model ? { model } : {}),
      }),
    })
    const data = (await res.json().catch(() => ({}))) as AssistantChatResponse
    if (!res.ok && !data.error) {
      return { ok: false, error: `http_${res.status}`, message: data.message }
    }
    return data
  } finally {
    window.clearTimeout(timer)
  }
}

/** Bloco E unificado — proposta LLM via bridge (mesma org/modelo/quota do Chat Hermes). */
export type AnalisePropostaResponse = {
  ok: boolean
  tipo?: 'insight_reflexao' | 'rascunho_reclamacao'
  fonte?: 'llm' | 'regras'
  modelo?: string | null
  leitura?: string
  texto?: string | null
  motivo?: string
  erro?: string
  error?: string
  message?: string
  gerado_em?: string
  cached?: boolean
  org_id?: string
  runtime?: string
}

export async function pedirAnaliseProposta(
  body: Record<string, unknown>,
): Promise<AnalisePropostaResponse> {
  const url = `${BASE.replace(/\/$/, '')}/analise/proposta`
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 90_000)
  try {
    const res = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
      },
      body: JSON.stringify(body),
    })
    const data = (await res.json().catch(() => ({}))) as AnalisePropostaResponse
    if (!res.ok && data.ok !== true) {
      return {
        ok: false,
        error: data.error || `http_${res.status}`,
        message: data.message,
        fonte: 'regras',
      }
    }
    return data
  } catch (e) {
    return {
      ok: false,
      error: 'network',
      message: e instanceof Error ? e.message : String(e),
      fonte: 'regras',
    }
  } finally {
    window.clearTimeout(timer)
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Falha ao ler áudio'))
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.readAsDataURL(blob)
  })
}

/** Transcreve áudio do Chat BI (mesmo STT OpenAI do Telegram). */
export async function transcribeAssistantAudio(
  blob: Blob,
  mime?: string,
): Promise<AssistantTranscribeResponse> {
  const url = `${BASE.replace(/\/$/, '')}/assistant/transcribe`
  const audio_base64 = await blobToBase64(blob)
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 90_000)
  try {
    const res = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(KEY ? { 'X-Fiesta-Bi-Key': KEY } : {}),
      },
      body: JSON.stringify({
        audio_base64,
        mime: mime || blob.type || 'audio/webm',
        language: 'pt',
      }),
    })
    const data = (await res.json().catch(() => ({}))) as AssistantTranscribeResponse
    if (!res.ok && !data.error) {
      return { ok: false, error: `http_${res.status}`, message: data.message }
    }
    return data
  } finally {
    window.clearTimeout(timer)
  }
}

/* --- Fase B: domínios financeiros --- */

export type DespesasRelatorio = {
  periodo: { inicio: string; fim: string; label: string }
  relatorio: {
    totalDespesas?: number
    totalValor?: number
    despesasPorCategoria?: Array<{ _id?: string; total?: number; valor?: number }>
    despesasPorFornecedor?: Array<{ _id?: string; total?: number; valor?: number }>
    despesasPorMes?: Array<{ _id?: string; total?: number; valor?: number }>
  }
}

export type DespesasPeriodo = {
  periodo: { inicio: string; fim: string; label: string }
  despesas: Array<Record<string, unknown>>
}

export type NotasPeriodo = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace?: string | null
  notas: Array<{
    _id?: string
    invoice_number?: string
    invoice_serie?: string
    invoice_total?: number
    issuance_date?: string
    marketplace_order_id?: string
    client?: { nome?: string; documento?: string }
    uf?: string
  }>
  count?: number
  resumo?: {
    total_notas?: number
    valor_total_nf?: number
    parcial?: boolean
  }
  paginacao?: {
    pagina?: number
    limite?: number
    total?: number
    totalPaginas?: number
    hasNextPage?: boolean
    hasPrevPage?: boolean
  }
}

export type FretesPeriodo = {
  periodo: { inicio: string; fim: string; label: string }
  fretes: Array<{
    _id?: string
    shipping_marketplace_id?: string
    total_cost?: number
    data_criacao?: string
    marketplace_data?: Record<string, unknown>
  }>
  paginacao?: { total?: number; pagina?: number; limite?: number }
}

export type MargensLista = {
  periodo: { inicio: string; fim: string; label: string }
  items: Array<{
    _id?: string
    description?: string
    ad_description?: string
    marketplace_name?: string
    paid_value?: number
    cmv?: number
    calculated_margin?: number
    calculated_margin_percent?: number
    taxes?: number
    shipping_cost?: number
    reference_date?: string
    code?: string
  }>
  totais?: Record<string, number>
  paginacao?: { total?: number }
}

export type MargensEstatisticas = {
  periodo: { inicio: string; fim: string; label: string }
  estatisticas: {
    totalMargens?: number
    margemTotal?: number
    margemMedia?: number
    totalCMV?: number
    totalImpostos?: number
    totalFretes?: number
    totalValorPago?: number
    totalQuantidade?: number
  }
}

export type ClientesRelatorio = {
  relatorio: {
    totalClientes?: number
    clientesPorEstado?: Record<string, number>
    clientesPorTipo?: Record<string, number>
  }
}

export function fetchDespesasRelatorio(query: PeriodQuery | Periodo) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<DespesasRelatorio>('/despesas/relatorio', periodParams(q))
}

export function fetchDespesasPeriodo(query: PeriodQuery | Periodo) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<DespesasPeriodo>('/despesas/periodo', {
    ...periodParams(q),
    limite: '30',
  })
}

export function fetchNotasPeriodo(
  query: PeriodQuery | Periodo,
  marketplace?: string,
  pagina = 1,
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<NotasPeriodo>('/notas-fiscais/periodo', {
    ...periodParams(q, marketplace),
    pagina: String(pagina),
    limite: '50',
  })
}

export function fetchFretesPeriodo(query: PeriodQuery | Periodo) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<FretesPeriodo>('/fretes/periodo', {
    ...periodParams(q),
    limite: '50',
  })
}

export type FretesPedidos = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace?: string | null
  fretes: Array<{
    pedido_id?: string
    canal?: string
    data?: string
    status?: string
    shipping_id?: string
    receita?: number
    frete?: number
  }>
  resumo?: {
    pedidos?: number
    pedidosComFrete?: number
    totalFrete?: number
    totalReceita?: number
    total_frete_fmt?: string
    porCanal?: Array<{ canal?: string; pedidos?: number; frete?: number }>
  }
  paginacao?: { pagina?: number; limite?: number; total?: number; pages?: number }
  fonte?: string
}

export function fetchFretesPedidos(
  query: PeriodQuery | Periodo,
  marketplace?: string,
  pagina = 1,
  limite = 50,
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<FretesPedidos>('/fretes/pedidos', {
    ...periodParams(q, marketplace),
    pagina: String(pagina),
    limite: String(limite),
  })
}

export function fetchMargensLista(query: PeriodQuery | Periodo, marketplace?: string) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<MargensLista>('/margens/lista', {
    ...periodParams(q, marketplace),
    limite: '30',
  })
}

/** Usa `/margens/periodo` (canónico). Alias `/margens/estatisticas` no bridge aponta para o mesmo. */
export async function fetchMargensEstatisticas(
  query: PeriodQuery | Periodo,
  marketplace?: string,
): Promise<MargensEstatisticas> {
  const raw = await fetchMargens(query, marketplace)
  return {
    periodo: raw.periodo,
    estatisticas: raw.margem || {},
  }
}

export function fetchClientesRelatorio() {
  return bridgeGet<ClientesRelatorio>('/clientes/relatorio', periodParams({}))
}

export function fmtIsoDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('pt-BR')
  } catch {
    return String(iso).slice(0, 10)
  }
}

/* --- Fase C: pedidos + operações --- */

export type PedidoListItem = {
  _id?: string
  marketplace_id?: string
  marketplace_name?: string
  order_status?: string
  order_date?: string
  order_revenue?: number
  marketplace_fees?: number
  marketplace_shipping_cost?: number
  order_products?: Array<{
    descricao_produto?: string
    quantidade?: number
    preco_total?: number
  }>
}

export type PedidosLista = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace: string | null
  pedidos: PedidoListItem[]
  paginacao?: {
    pagina?: number
    limite?: number
    total?: number
    totalPaginas?: number
    hasNextPage?: boolean
    hasPrevPage?: boolean
  }
}

export type PedidoDetalhe = {
  pedido: PedidoListItem & Record<string, unknown>
}

export type OrigemStats = {
  origem: {
    hoje?: number
    por_marketplace?: Array<{ marketplace?: string; total?: number }>
    por_status?: Array<{ status_pedido?: string; total?: number }>
  }
}

export type OrigemPedidos = {
  pedidos:
    | {
        data?: Array<{
          id?: string
          id_pedido?: string
          marketplace?: string
          status_pedido?: string
          data_venda?: string
          nome_produto?: string
          valor?: number
          quantidade?: number
        }>
      }
    | Array<Record<string, unknown>>
}

export function fetchPedidosLista(
  query: PeriodQuery | Periodo,
  marketplace?: string,
  opts?: {
    pagina?: number
    limite?: number
    order_status?: string
    marketplace_id?: string
    /** Se true, não envia datas — útil para achar pedido por ID fora do período do filtro */
    semPeriodo?: boolean
  },
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  const params: Record<string, string> = {
    pagina: String(opts?.pagina ?? 1),
    limite: String(opts?.limite ?? 30),
  }
  if (!opts?.semPeriodo) {
    Object.assign(params, periodParams(q, marketplace))
  } else if (marketplace) {
    params.marketplace = marketplace
  }
  if (opts?.order_status) params.order_status = opts.order_status
  if (opts?.marketplace_id) params.marketplace_id = opts.marketplace_id
  return bridgeGet<PedidosLista>('/pedidos', params)
}

export function fetchPedidoDetalhe(id: string) {
  return bridgeGet<PedidoDetalhe>(`/pedidos/${encodeURIComponent(id)}`)
}

export type ProdutoDetalhe = {
  sku: string
  catalog_sku?: string
  listing_id?: string | null
  resolved_from?: string | null
  produto: Record<string, unknown> | null
  cmv?: Record<string, unknown> | null
}

export type PagamentosLista = {
  periodo?: { inicio?: string; fim?: string; label?: string }
  pagamentos: Array<Record<string, unknown>>
  paginacao?: {
    page?: number
    limit?: number
    total?: number
    pages?: number
  }
}

export function fetchProdutoPorSku(sku: string) {
  return bridgeGet<ProdutoDetalhe>(`/produtos/sku/${encodeURIComponent(sku)}`)
}

export type ProdutosLista = {
  produtos: Array<Record<string, unknown>>
  paginacao?: {
    pagina?: number
    limite?: number
    total?: number
    totalPaginas?: number
    hasNextPage?: boolean
    hasPrevPage?: boolean
  }
}

export function fetchProdutosLista(opts?: {
  pagina?: number
  limite?: number
  q?: string
  sku?: string
  categoria?: string
}) {
  const params: Record<string, string> = {
    pagina: String(opts?.pagina ?? 1),
    limite: String(opts?.limite ?? 25),
  }
  if (opts?.q) params.q = opts.q
  if (opts?.sku) params.sku = opts.sku
  if (opts?.categoria) params.categoria = opts.categoria
  return bridgeGet<ProdutosLista>('/produtos', params)
}

export type ProdutosVendidos = {
  periodo: { inicio: string; fim: string; label: string }
  marketplace?: string | null
  produtos: Array<{
    product_id?: string
    descricao?: string
    /** Título/capa/SKU/permalink — opcionais; a tela degrada sem eles. */
    titulo?: string
    sku?: string
    thumbnail?: string
    permalink?: string
    canais?: string[]
    quantidade?: number
    receita?: number
    pedidos?: number
    ticket_medio?: number
  }>
  /** Pedidos multi-anúncio fora das linhas (quando o backend declarar). */
  excluidos_multi_anuncio?: { pedidos?: number; quantidade?: number; receita?: number }
  resumo?: {
    total_produtos?: number
    receita_total?: number
    quantidade_total?: number
    receita_total_fmt?: string
  }
  paginacao?: { pagina?: number; limite?: number; total?: number; pages?: number }
  fonte?: string
}

export type VariacoesAnuncio = {
  ad_code?: string
  variacoes?: Array<{
    chave?: string
    pedidos?: number
    quantidade?: number
    receita?: number
  }>
  erro?: string
}

/**
 * Detalhe por variação — endpoint ainda não existe no bridge oficial.
 * A tela trata 404/erro com mensagem honesta.
 */
export function fetchVariacoesAnuncio(
  adCode: string,
  query: PeriodQuery | Periodo,
  marketplace?: string,
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<VariacoesAnuncio>('/produtos/vendidos/variacoes', {
    ...periodParams(q, marketplace),
    ad_code: adCode,
  })
}

export function fetchProdutosVendidos(
  query: PeriodQuery | Periodo,
  marketplace?: string,
  opts?: { pagina?: number; limite?: number; busca?: string; ordenarPor?: 'receita' | 'quantidade' },
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  const params: Record<string, string> = {
    ...periodParams(q, marketplace),
    pagina: String(opts?.pagina ?? 1),
    limite: String(opts?.limite ?? 30),
  }
  if (opts?.busca) params.busca = opts.busca
  if (opts?.ordenarPor) params.ordenar_por = opts.ordenarPor
  return bridgeGet<ProdutosVendidos>('/produtos/vendidos', params)
}

/** Avaliação pública do ML: só nota e texto — a API não traz nome, e o bridge não repassa nada além disso. */
export type InsumoAvaliacao = { nota: number; titulo: string; texto: string; curtidas: number; data: string }
export type InsumoPergunta = { pergunta: string; resposta: string; status: string; data: string }
/** Agrupamento por palavra repetida — hipótese nossa, não leitura de sentido. A contagem é real. */
export type InsumoTema = { tema: string; quantidade: number; exemplos: string[]; tipo: 'hipotese' }

export type RoteiroInsumos = {
  ok: boolean
  error?: string
  message?: string
  mlb: string
  conta?: string
  gerado_em?: string
  fonte?: 'api' | 'cache'
  avisos?: string[]
  anuncio?: {
    encontrado: boolean
    mlb: string
    titulo: string
    preco: number | null
    preco_original: number | null
    vendidos: number
    estoque: number
    status: string
    conta: string
    permalink: string
    foto_principal: string
    catalog_product_id: string
    catalogo: boolean
    atributos: Array<{ id: string; nome: string; valor: string }>
    descricao: string
  }
  avaliacoes?: {
    disponivel: boolean
    total: number
    media: number | null
    niveis: Record<string, number>
    positivas: InsumoAvaliacao[]
    negativas: InsumoAvaliacao[]
    neutras?: InsumoAvaliacao[]
  }
  perguntas?: { disponivel: boolean; total: number; lista: InsumoPergunta[]; temas: InsumoTema[] }
  campeoes?: Array<{
    mlb: string
    titulo: string
    preco: number | null
    vendidos: number
    permalink: string
    avaliacoes: { media: number | null; total: number; negativas: InsumoAvaliacao[]; positivas: InsumoAvaliacao[] }
    perguntas: { total: number; temas: InsumoTema[] }
  }>
  /** Registro salvo pela Fábrica de Anúncio (spec PESQUISA_PARA_ROTEIRO.md). `existe: false` → rodar /fabricadeanuncio. */
  pesquisa?: {
    existe: boolean
    aviso?: string
    data_pesquisa?: string
    idade_dias?: number
    objecoes?: Array<{ texto: string; fonte?: string; quantidade?: number; tipo?: string }>
    elogios?: Array<{ texto: string; fonte?: string; quantidade?: number; tipo?: string }>
    diferenciais?: Array<{ texto: string; fonte?: string; tipo?: string }>
    ocasioes?: Array<string | { texto: string; fonte?: string }>
    faixa_preco?: { min?: number; mediana?: number; max?: number }
    campeoes?: Array<{ mlb?: string; titulo?: string; preco?: number; vendidos?: number }>
    fotos_produto?: string[]
    fontes?: Array<string | { url?: string; endpoint?: string; data?: string }>
  }
  legenda?: { real: string; hipotese: string }
}

/**
 * Insumos do roteiro da Fábrica de Vídeos: pesquisa da Fábrica de Anúncio + o próprio anúncio
 * (ficha, descrição, avaliações, perguntas, campeões do catálogo) lidos na API do ML pelo bridge.
 * Só leitura; cache de 24 h no backend, `refresh` força nova leitura.
 */
export function fetchRoteiroInsumos(mlb: string, opts?: { refresh?: boolean }) {
  const params: Record<string, string> = { mlb }
  if (opts?.refresh) params.refresh = '1'
  return bridgeGet<RoteiroInsumos>('/fabrica-videos/insumos', params)
}

export type BridgeHealth = {
  ok?: boolean
  service?: string
  version?: string
  date?: string
  s1_configured?: boolean
  hermes_bin?: boolean
  stt_configured?: boolean
}

export function fetchBridgeHealth() {
  return bridgeGet<BridgeHealth>('/health')
}

export function fetchPagamentos(
  query: PeriodQuery | Periodo,
  opts?: { pagina?: number; limite?: number; status?: string },
) {
  const q = typeof query === 'string' ? { periodo: query } : query
  const params: Record<string, string> = {
    ...periodParams(q),
    pagina: String(opts?.pagina ?? 1),
    limite: String(opts?.limite ?? 30),
  }
  if (opts?.status) params.status = opts.status
  return bridgeGet<PagamentosLista>('/pagamentos', params)
}

export function fetchOrigemStats() {
  return bridgeGet<OrigemStats>('/origem/stats')
}

export function fetchOrigemPedidos(opts?: {
  per_page?: number
  marketplace?: string
  dataInicio?: string
  dataFim?: string
}) {
  const params: Record<string, string> = {
    page: '1',
    per_page: String(opts?.per_page ?? 15),
  }
  if (opts?.marketplace) params.marketplace = opts.marketplace
  if (opts?.dataInicio) params.data_inicio = opts.dataInicio
  if (opts?.dataFim) params.data_fim = opts.dataFim
  return bridgeGet<OrigemPedidos>('/origem/pedidos', params)
}

export type FiscalConciliacao = {
  periodo?: { inicio?: string; fim?: string; label?: string }
  summaries?: Array<{
    hub?: string
    label?: string
    hub_label?: string
    matched?: number
    sem_nf?: number
    nf_orfa?: number
    pedidos?: number
    [key: string]: unknown
  }>
  marketplace?: string | null
  escopo?: {
    pedidos?: string
    nfs?: string
    nf_orfa_confiavel?: boolean
    nota?: string
  }
  s1_url?: string
  ok?: boolean
  error?: string
  message?: string
}

export function fetchFiscalConciliacao(query: PeriodQuery | Periodo, marketplace?: string) {
  const q: PeriodQuery = typeof query === 'string' ? { periodo: query } : query
  return bridgeGet<FiscalConciliacao>('/fiscal/conciliacao', periodParams(q, marketplace))
}

export const S2_APP_URL =
  (import.meta.env.VITE_S2_APP_URL as string) || 'https://fiestaup.toteus.cloud'
export const S1_APP_URL =
  (import.meta.env.VITE_S1_APP_URL as string) || 'https://api.fiestaup.toteus.cloud'
